import Backbone from 'backbone'
import template from './transformation-then.hbs'
import dropdown from 'scoreboard/components/dropdown'
import enums from 'scoreboard/components/enums'
import formHelper from 'scoreboard/components/form-helper'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import positiveIntegerValidator from 'scoreboard/components/validators/positive-integer-validator'
import dateFormatPatternValidator from 'scoreboard/components/validators/date-format-pattern-validator'
import numericEvaluator from 'scoreboard/components/evaluators/numeric-evaluator'
import numericValidator from 'scoreboard/components/validators/numeric-validator'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import _ from 'lodash'
import renderTransformationThen from './render-transformation-then'
import selects from 'scoreboard/components/selects'
import thenOperatorOptions from './then-operator-options'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER } = DataType

import {
    formatLocalizedNumericValueForServer,
    formatNumericValueForLocalizedDisplay,
} from './numeric-value-localization'

var TransformationThen = Backbone.View.extend({

	/**
	 * The "then" portion of a transformation
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {FormView} options.formView
	 *
	 * @param {Array} options.fields the fields which must have id and label
	 * properties, and may optionally have a dataTypes property. The id property
	 * can be a String if needed (e.g. for datasets)
	 *
	 * @param {Object} [options.then] the "then" being edited
	 *
	 * @param {String} [options.type] the type of the Importable (dataset, metric-value)
	 *
	 */
	initialize(options = {}) {
		this.options = options
	},

	className: 'transformation-then',

	events: {
		'change [name=operator]': 'onChangeOperator',
		'change [name=fieldId]' : 'onChangeFieldId'
	},

	render: function() {
		this.$el.empty().append(template())

		// render field select
		this.renderFieldSelect(this.options.fields)

		// render operator select
		this.renderOperatorSelect()

		// render the "type of characters" dropdown
		dropdown.object(this.$('[name=typeOfCharacter]'), enums.typeOfCharacter,
			undefined, { sort: true })

		// #36330 for make-x thens, display localized numeric values if needed
		if (this.options.then.operator === 'make-x') {
			this.options.then.value = formatNumericValueForLocalizedDisplay(this.options.then.value)
		}

		formHelper.writeToDom(this.options.then, this.el)
		this.markFields()

		this.onChangeOperator()
		this.onChangeFieldId()

		return this
	},

	renderFieldSelect: function (fields, selected) {
		const selectOptions = {
			options: fields,
			groupBy: this.options.groupBy,
			accessors: { value: 'id' },
			selected
		}
		if (_.find(fields, f => f.sortOrder)) {
			// #35917 At least one field contains an explicit `sortOrder` property, so
			// we need to sort by that first
			selectOptions.sort = [
				{field: 'sortOrder', direction: 'asc'},
				{field: 'label', direction: 'asc'}
			]
		}
		selects.filtering(this.$('[name=fieldId]'), selectOptions)
	},

	renderOperatorSelect: function() {
		let type = _.get(this.options, 'type')
		let operatorEl = this.$('[name=operator]')
		let opts = thenOperatorOptions(type)
		selects(operatorEl.empty(), {
			options: opts,
			accessors: {value: 'id'},
			groupBy: 'group',
			sort: [] // maintain order specified by thenOperatorOptions
		})
	},

	markFields: function() {
		// most of the fields are required text fields
		this.markedFields = _.map(this.$('[type=text]:not([name=characters], [name=format], [name=value])'), el => {
			return new MarkedField({
				el,
				validators: [requiredValidator]
			}).render()
		})
		// the characters field must be a positive integer
		this.markedFields.push(new MarkedField({
			el: this.$('[name=characters]'),
			dataType: NUMBER,
			evaluator: numericEvaluator,
			validators: [requiredValidator, positiveIntegerValidator]
		}).render())

		// validators for date format field
		this.markedFields.push(new MarkedField({
			el: this.$('[name=format]'),
			validators: [requiredValidator, dateFormatPatternValidator]
		}).render())

		let dataType = this.getFieldDatatype()
		this.valueField = new MarkedField({
			el: this.$('[name=value]'),
			dataType,
			validators: isNumericDataType(dataType) ?
				[requiredValidator, numericValidator] :
				[requiredValidator]
		}).render()
		this.markedFields.push(this.valueField)

		// a field must be selected
		this.markedFields.push(new MarkedField({
			el: this.$('[name=fieldId]'),
			validators: [requiredValidator]
		}).render())
	},

	onChangeFieldId: function() {
		// handle changing to or changing from a field id that represents a
		// primary action such as "by removing record" (#26842 #13966)
		let fieldId = this.getFieldId()

		if (this.isPrimaryAction(fieldId) || this.isPrimaryAction(this.oldFieldId)) {
			// update the display
			if (this.isPrimaryAction(fieldId)) {
				renderTransformationThen(this.el, 'primary-action')
				_.invokeMap(this.markedFields, 'clearError')
			} else {
				this.onChangeOperator()
			}
		}
		// Update the data type of the field and other attributed accordingly
		this.valueField.clearError()
		let dataType = this.getFieldDatatype()
		this.valueField.mark({
			dataType,
			validators: isNumericDataType(dataType) ?
				[requiredValidator, numericValidator] :
				[requiredValidator]
		})
		this.oldFieldId = fieldId
	},

	getFieldId: function() {
		return this.$('[name=fieldId]').val()
	},

	getFieldDatatype: function() {
		let fieldId = this.getFieldId()
		let fields = _.get(this.options, 'fields')
		let field = _.find(fields, field => {
			return field.id == fieldId
		})
		return field ? field.dataType : null
	},

	// Indicates if the fieldId selected represents a transformation Then that
	// is a primary action without a need for further criteria
	isPrimaryAction: function(fieldId) {
		let primaryActions = ['do-not-import-record', 'remove-record', 'make-kpi-value-na']
		return _.includes(primaryActions, fieldId)
	},

	onChangeOperator: function() {
		let operator = this.$('[name=operator]').val()
		renderTransformationThen(this.el, operator)
		_.invokeMap(this.markedFields, 'clearError')
	},

	/**
	 * Returns the data that should be submitted to the server
	 */
	readFromDom: function() {
		let data = formHelper.readFromDom(this.el)

		// #36330 for make-x thens, format localized numeric values to English
		// locale if needed for server
		if (data.operator === 'make-x') {
			data.value = formatLocalizedNumericValueForServer(data.value)
		}

		// populate then operator for 'by not importing record' (#26842 #13966)
		// and other thens that are a primary action without criteria
		if (this.isPrimaryAction(data.fieldId)) {
			data.operator = data.fieldId
		}
		return data
	},

	test: function() {
		return formHelper.test(this.$el)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([...this.markedFields], 'remove')
	}

})

export default TransformationThen

TransformationThen.__filename__ = 'imports/transform/transformation-then.js'
